<template>
  <div>
    <b-modal id="advanced-filter-modal" scrollable no-close-on-backdrop v-model="isVisible"
      title="Advanced Filters : Bed Nodes" @show="syncFilters">
      <b-form>
        <b-row v-if="filterData && filterData.length > 0">
          <b-col md="12" class="mb-3">
            <label class="font-weight-bold text-secondary mb-2">Operation Mode:</label>
            <b-form-radio-group v-model="operationType" name="operation-type" btnsize="sm">
              <b-form-radio value="AND"><i class="bi bi-check-all me-1"></i> Display Beds which matches each of the selected versions </b-form-radio>
              <b-form-radio value="OR"><i class="bi bi-plus-circle me-1"></i> Display Beds which matches at least on of the selected versions </b-form-radio>
            </b-form-radio-group>
          </b-col>

          <b-col v-for="(field, index) in filterData" :key="index" md="4" class="mb-3">
            <label class="font-weight-medium mb-1 d-block">{{ field.component }}</label>

            <div class="searchable-select-container" @mousedown.capture="closeDropdownIfOpen">
              <v-select v-model="selectedComponentAndVersions[field.component]" :options="getOptions(field.component)"
                :reduce="option => option.value" label="text" placeholder="Select version..." class="custom-v-select"
                no-dropdown-indicator>

                <template #option="{ text, selected }">
                  <div class="d-flex align-items-center">
                    <i class="bi bi-check2 me-2" v-if="selected"></i>
                    <span :class="{ 'text-primary font-weight-bold': selected }">{{ text }}</span>
                  </div>
                </template>
              </v-select>
            </div>
          </b-col>
        </b-row>

        <b-row v-else>
          <b-col class="text-center py-4">
            <i class="bi bi-exclamation-circle d-block mb-2" style="font-size: 2rem"></i>
            <p>No component versions available.</p>
          </b-col>
        </b-row>
      </b-form>

      <template #modal-footer="{ cancel }">
        <b-button class="btn" @click="cancel()">Cancel</b-button>
        <b-button class="btn btn-primary" @click="applyFilters" :disabled="filterData.length === 0">
          Apply Filters
        </b-button>
        <b-button class="btn btn-reset" @click="resetFilters" :disabled="filterData.length === 0">Reset
          Filters</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import VueSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

export default {
  name: "AdvancedDeviceSearchDialog",
  components: {
    'v-select': VueSelect
  },
  data() {
    return {
      filterData: [],
      selectedComponentAndVersions: {},
      operationType: null,
      isVisible: false,
      componentVersionsCache: {}
    };
  },
  methods: {
    show() {
      this.isVisible = true;
    },

    setInputData(existingFilters, filterOptions) {
      this.filterData = filterOptions;
      this.operationType = existingFilters.operation || "AND";

      const newCache = {};
      const newSelections = {};

      filterOptions.forEach(field => {
        const normalizedOptions = (field.versions || []).map(v => {
          return typeof v === 'string' ? { text: v, value: v } : v;
        });

        newCache[field.component] = normalizedOptions;
        newSelections[field.component] = null;
      });

      this.componentVersionsCache = newCache;

      const hasExisting = existingFilters.components && Object.keys(existingFilters.components).length > 0;
      
      if (hasExisting) {
        this.selectedComponentAndVersions = {
          ...newSelections,
          ...existingFilters.components
        };
      } else {
        this.selectedComponentAndVersions = newSelections;
      }
    },

    getOptions(componentKey) {
      // Fixed syntax error here
      return this.componentVersionsCache[componentKey] || [];
    },

    closeDropdownIfOpen(event) {
      // vue-select ignores clicks on its search input while open, so it never toggles closed
      const target = event.target;
      if (!target.classList.contains('vs__search')) return;
      const select = target.closest('.v-select');
      if (select && select.classList.contains('vs--open')) {
        event.preventDefault();
        event.stopPropagation();
        target.blur();
      }
    },

    syncFilters() {
      // Sync logic if needed
    },

    applyFilters() {
      try {
        // Create the object containing only the keys that actually have a selected value
        const selectedComponents = {};

        Object.entries(this.selectedComponentAndVersions).forEach(([key, value]) => {
          // Only add to the payload if the value is not null, undefined, or an empty string
          if (value !== null && value !== undefined && value !== '') {
            selectedComponents[key] = value;
          }
        });

        this.$emit("filterApplied", { components: selectedComponents, operation: this.operationType });
      } catch (error) {
        console.error("Error applying filters:", error);
      }
      this.isVisible = false;
    },

    resetFilters() {
      // Preserve the component keys, but nullify the values so vue-select clears
      const resetSelections = {};
      Object.keys(this.selectedComponentAndVersions).forEach(key => {
        resetSelections[key] = null;
      });
      this.selectedComponentAndVersions = resetSelections;
      this.operationType = "AND";
      this.$emit("filterResetApplied");
    },
  },
};
</script>

<style scoped>
.btn {
  &:disabled {
    background: #f0f0f0 !important;
    border-color: #ddd !important;
    color: #666 !important;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    color: white;
    border-color: #1dd8f6;
    background: #1dd8f6;
  }
}

::v-deep .custom-control-input[type="radio"]:checked+.custom-control-label::before {
  border-color: #1dd8f6 !important;
  background-color: #40bacc !important;
}

/* Searchable Dropdown Customization */
.searchable-select-container {
  position: relative;
}

.custom-v-select ::v-deep .vs__dropdown-toggle {
  padding: 4px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  min-height: 38px;
  background: #fff;
}

.custom-v-select ::v-deep .vs__search::placeholder {
  color: #adb5bd;
}

.custom-v-select ::v-deep .vs__selected {
  font-size: 0.875rem;
  background: transparent;
  padding: 0;
}

.custom-v-select.vs--single ::v-deep .vs__selected-options {
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
}

.custom-v-select.vs--single ::v-deep .vs__selected {
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-v-select.vs--single ::v-deep .vs__search {
  min-width: 0;
}

/* Hover color matching the primary button theme */
.custom-v-select ::v-deep .vs__dropdown-option--highlight {
  background: #1dd8f6 !important;
  color: white !important;
}

.selected-text {
  color: #212529;
}

.custom-v-select ::v-deep .vs__dropdown-option--selected {
  background: rgba(29, 216, 246, 0.1);
  color: #1dd8f6;
}

.btn-reset {
  color: white;
  border-color: #1dd8f6;
  background: #1dd8f6;

  &:disabled {
    background: #f0f0f0 !important;
    border-color: #ddd !important;
    color: #666 !important;
    cursor: not-allowed;
  }
}
</style>